import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["canvas", "image", "toolbar", "sidebar", "pointCounts", "routeList", "measurementList", "areaList", "scaleInfo"]
  static values = {
    annotationsUrl: String,
    scaleUrl: String,
    glyphsUrl: String,
    pixelsPerMetre: Number,
    sheetId: Number,
    pointTypes: { type: Array, default: [] }
  }

  connect() {
    this.annotations = []
    this.currentTool = null
    this.currentPointType = null
    this.routePoints = []
    this.linePoints = []
    this.areaPoints = []
    this.scalePoints = []
    this.teachCrops = []      // committed marquee rectangles, image coords
    this.teachDraft = null    // in-progress marquee rectangle
    this.hiddenPointTypes = new Set()  // per-type visibility (client-only,
                                       // resets on refresh — it's a view state,
                                       // not a data state)
    this.pan = { x: 0, y: 0 }
    this.zoom = 1
    this.isPanning = false
    this.isDragging = false
    this.dragAnnotation = null
    this.lastMouse = null

    this.canvas = this.canvasTarget
    this.ctx = this.canvas.getContext("2d")

    this.img = new Image()
    this.img.crossOrigin = "anonymous"
    this.img.onload = () => {
      this.canvas.width = this.canvas.parentElement.clientWidth
      this.canvas.height = Math.max(500, this.canvas.parentElement.clientWidth * (this.img.height / this.img.width))
      this.fitImageToCanvas()
      this.loadAnnotations()
    }
    this.img.src = this.imageTarget.dataset.src

    this.canvas.addEventListener("wheel", (e) => this.handleWheel(e), { passive: false })

    // Glyph thumbnails + "Looking for" overlay data. Keyed by point_type slug;
    // populated by loadGlyphs() and refreshed after each teach_symbol run.
    this.glyphsByType = {}
    this.loadGlyphs()
  }

  async loadGlyphs() {
    if (!this.hasGlyphsUrlValue) return
    try {
      const resp = await fetch(this.glyphsUrlValue, { headers: { "Accept": "application/json" } })
      if (!resp.ok) return
      this.glyphsByType = await resp.json()
      this.hydrateRowThumbnails()
      this.updateLookingForOverlay()
    } catch (e) {
      // Non-fatal — thumbnails just stay as "?" placeholders.
      console.warn("Failed to load glyphs:", e)
    }
  }

  hydrateTeachExistingExamples() {
    const container = document.querySelector("[data-teach-existing]")
    if (!container) return

    const list = container.querySelector("[data-teach-existing-list]")
    const countEl = container.querySelector("[data-teach-existing-count]")
    if (!list) return

    const pt = this.currentPointType
    const g = pt ? this.glyphsByType[pt] : null
    const examples = (g && g.examples) || []

    container.classList.toggle("hidden", examples.length === 0)
    if (countEl) {
      const taught = examples.filter(e => e.source === "taught").length
      const legend = examples.filter(e => e.source === "legend").length
      const parts = []
      if (taught > 0) parts.push(`${taught} taught`)
      if (legend > 0) parts.push(`${legend} from legend`)
      countEl.textContent = parts.join(" + ")
    }

    list.innerHTML = ""
    examples.slice(0, 8).forEach(ex => {
      const wrap = document.createElement("div")
      const canonicalRing = ex.is_canonical ? "ring-2 ring-mint" : ""
      wrap.className = `group relative h-10 w-10 shrink-0 rounded border border-line bg-white overflow-hidden ${canonicalRing}`
      wrap.title = ex.is_canonical
        ? "Canonical — this is the reference glyph"
        : ex.source === "legend" ? "From this sheet's legend" : "Taught previously"

      const img = document.createElement("img")
      img.src = ex.thumbnail_url
      img.className = "h-full w-full object-contain"
      wrap.appendChild(img)

      if (ex.source === "legend") {
        const tag = document.createElement("span")
        tag.className = "absolute bottom-0 left-0 right-0 bg-mint text-[8px] font-bold text-white text-center pointer-events-none"
        tag.textContent = "KEY"
        wrap.appendChild(tag)
      }

      // Hover controls — Promote (★) + Discard (×). Hidden by default,
      // revealed by group-hover. Click handlers call the teaching endpoints
      // then reload glyphs so the row + panel refresh live.
      if (!ex.is_canonical) {
        const promoteBtn = document.createElement("button")
        promoteBtn.type = "button"
        promoteBtn.className = "absolute top-0 left-0 hidden group-hover:block bg-mint text-white text-[10px] font-bold w-4 h-4 leading-4 text-center rounded-br"
        promoteBtn.textContent = "★"
        promoteBtn.title = "Set as canonical"
        promoteBtn.addEventListener("click", (e) => {
          e.preventDefault()
          this.promoteTeaching(ex.id)
        })
        wrap.appendChild(promoteBtn)
      }

      const discardBtn = document.createElement("button")
      discardBtn.type = "button"
      discardBtn.className = "absolute top-0 right-0 hidden group-hover:block bg-red-500 text-white text-[10px] font-bold w-4 h-4 leading-4 text-center rounded-bl"
      discardBtn.textContent = "×"
      discardBtn.title = "Discard this example"
      discardBtn.addEventListener("click", (e) => {
        e.preventDefault()
        if (!confirm("Discard this taught example? It will stop being used for matching.")) return
        this.discardTeaching(ex.id)
      })
      wrap.appendChild(discardBtn)

      list.appendChild(wrap)
    })
  }

  async promoteTeaching(id) {
    try {
      const resp = await fetch(`/settings/symbol_teachings/${id}/promote`, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        }
      })
      if (!resp.ok) throw new Error(`promote failed: ${resp.status}`)
      await this.loadGlyphs()
    } catch (e) {
      console.warn("Promote failed:", e)
    }
  }

  async discardTeaching(id) {
    try {
      const resp = await fetch(`/settings/symbol_teachings/${id}`, {
        method: "DELETE",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        }
      })
      if (!resp.ok) throw new Error(`discard failed: ${resp.status}`)
      await this.loadGlyphs()
    } catch (e) {
      console.warn("Discard failed:", e)
    }
  }

  // Pinned canvas overlay showing the user what they're currently counting or
  // teaching. Appears whenever Manual Count or Teach is scoped to a point
  // type; hides for non-scoped tools. Thumbnails come from glyphsByType; if
  // none exist yet, prompts the user to teach once to capture a reference.
  updateLookingForOverlay() {
    const overlay = document.getElementById("looking-for-overlay")
    if (!overlay) return

    const relevantTool = this.currentTool === "point" || this.currentTool === "teach"
    const active = relevantTool && this.currentPointType
    overlay.classList.toggle("hidden", !active)
    if (!active) return

    const modeEl = overlay.querySelector("[data-lf-mode]")
    if (modeEl) modeEl.textContent = this.currentTool === "teach" ? "Teaching" : "Looking for"

    const nameEl = overlay.querySelector("[data-lf-name]")
    if (nameEl) nameEl.textContent = this.pointTypeName(this.currentPointType) || "—"

    const thumb = overlay.querySelector("[data-lf-thumb]")
    const missing = overlay.querySelector("[data-lf-missing]")
    const meta = overlay.querySelector("[data-lf-meta]")

    const g = this.glyphsByType[this.currentPointType]
    if (g && g.thumbnail_url) {
      if (thumb) {
        thumb.src = g.thumbnail_url
        thumb.classList.remove("hidden")
      }
      if (missing) missing.classList.add("hidden")
      if (meta) meta.textContent = g.source === "legend" ? "From this sheet's legend" : "Taught by you"
    } else {
      if (thumb) thumb.classList.add("hidden")
      if (missing) missing.classList.remove("hidden")
      if (meta) meta.textContent = ""
    }
  }

  hydrateRowThumbnails() {
    this.element.querySelectorAll("[data-glyph-slot]").forEach(slot => {
      const pt = slot.dataset.glyphSlot
      const g = this.glyphsByType[pt]
      if (!g || !g.thumbnail_url) return
      slot.innerHTML = ""
      const img = document.createElement("img")
      img.src = g.thumbnail_url
      img.alt = pt
      img.className = "h-full w-full object-contain"
      slot.appendChild(img)

      // Mint ring when the preferred glyph is user-flagged canonical, so the
      // user can see at a glance which types have an explicit reference set.
      slot.classList.toggle("ring-2", !!g.is_canonical)
      slot.classList.toggle("ring-mint", !!g.is_canonical)
    })

    // Per-row "Apply learned" — always visible, but disabled until the user
    // has taught (or auto-captured a legend glyph) at least once. Tooltip
    // explains the affordance either way.
    this.element.querySelectorAll("[data-apply-learned-button]").forEach(btn => {
      const pt = btn.dataset.applyLearnedButton
      const g = this.glyphsByType[pt]
      const count = g && g.teaching_count ? g.teaching_count : 0
      btn.disabled = count === 0
      btn.title = count === 0
        ? "Teach at least one example first"
        : `Detect this type using ${count} learned example${count === 1 ? "" : "s"}`
      const badge = btn.querySelector("[data-apply-learned-count]")
      if (badge) badge.textContent = count > 0 ? count : ""
    })

    // Bulk "Apply learned" — visible if any type has any teachings.
    const anyTeachings = Object.values(this.glyphsByType).some(g => g && g.teaching_count > 0)
    const bulkBtn = this.element.querySelector("[data-apply-all-learned-button]")
    if (bulkBtn) {
      const form = bulkBtn.closest("form") || bulkBtn
      form.classList.toggle("hidden", !anyTeachings)
    }
  }

  fitImageToCanvas() {
    const scaleX = this.canvas.width / this.img.width
    const scaleY = this.canvas.height / this.img.height
    this.zoom = Math.min(scaleX, scaleY) * 0.95
    this.pan = {
      x: (this.canvas.width - this.img.width * this.zoom) / 2,
      y: (this.canvas.height - this.img.height * this.zoom) / 2
    }
    this.redraw()
  }

  // --- Tool selection ---

  selectTool(event) {
    const tool = event.currentTarget.dataset.tool
    const pointType = event.currentTarget.dataset.pointType

    // Finish any in-progress polyline
    if (this.currentTool === "cable_route" && this.routePoints.length > 1 && tool !== "cable_route") {
      this.finishRoute()
    }
    if (this.currentTool === "line" && this.linePoints.length > 1 && tool !== "line") {
      this.finishLine()
    }
    if (this.currentTool === "area" && this.areaPoints.length > 2 && tool !== "area") {
      this.finishArea()
    }

    // Switching Teach to a different point type means the user is teaching a
    // different symbol now — drop any in-progress crops so they don't get
    // submitted under the new type by mistake.
    const teachingDifferentType = tool === "teach" &&
                                  this.currentTool === "teach" &&
                                  this.currentPointType !== (pointType || null)
    if (teachingDifferentType || tool !== "teach") {
      // Clear crops when leaving teach entirely, or switching to a new type.
      // Exception: staying on teach with the same type keeps crops intact so
      // the user can pan/zoom mid-selection without losing progress.
      if (tool !== "teach" && this.currentTool === "teach") {
        // leaving teach — keep crops so the user can come back
      } else if (teachingDifferentType) {
        this.teachCrops = []
      }
    }

    this.currentTool = tool
    this.currentPointType = pointType || null
    this.routePoints = []
    this.linePoints = []
    this.areaPoints = []
    this.scalePoints = []
    this.teachDraft = null
    this.updateTeachPanel()
    this.updateLookingForOverlay()

    // Update toolbar button states
    this.element.querySelectorAll("[data-tool]").forEach(btn => {
      btn.classList.toggle("ring-2", btn.dataset.tool === tool && btn.dataset.pointType === (pointType || ""))
      btn.classList.toggle("ring-indigo-500", btn.dataset.tool === tool && btn.dataset.pointType === (pointType || ""))
    })

    this.canvas.style.cursor = tool === "pan" ? "grab" : "crosshair"
  }

  // --- Canvas interaction ---

  canvasMouseDown(event) {
    if (event.button === 1 || (event.button === 0 && this.currentTool === "pan")) {
      this.isPanning = true
      this.lastMouse = { x: event.clientX, y: event.clientY }
      this.canvas.style.cursor = "grabbing"
      event.preventDefault()
      return
    }

    if (event.button !== 0) return

    const pos = this.canvasToImage(event)

    // Check if clicking on an existing point marker. Small moves turn into
    // "review" clicks (popover). Larger moves turn into drags — decided in
    // canvasMouseUp from the total delta.
    const hitAnnotation = this.findAnnotationAt(pos)
    if (hitAnnotation) {
      this.isDragging = true
      this.dragAnnotation = hitAnnotation
      this.dragStart = { clientX: event.clientX, clientY: event.clientY }
      this.canvas.style.cursor = "grabbing"
      return
    }

    // Click anywhere else on the canvas closes the review popover.
    this.closeReviewPopover()

    if (this.currentTool === "teach") {
      if (this.teachCrops.length >= 3) {
        alert("You can teach with up to 3 examples per symbol.")
        return
      }
      this.teachDraft = { x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y }
      this.redraw()
      return
    }

    if (this.currentTool === "point" && this.currentPointType) {
      this.createPointAnnotation(pos)
    } else if (this.currentTool === "cable_route") {
      this.routePoints.push(pos)
      this.redraw()
    } else if (this.currentTool === "line") {
      this.linePoints.push(pos)
      this.redraw()
    } else if (this.currentTool === "area") {
      this.areaPoints.push(pos)
      this.redraw()
    } else if (this.currentTool === "scale") {
      this.scalePoints.push(pos)
      if (this.scalePoints.length === 2) {
        this.promptScaleDistance()
      }
      this.redraw()
    }
  }

  canvasMouseMove(event) {
    if (this.teachDraft) {
      const pos = this.canvasToImage(event)
      this.teachDraft.x2 = pos.x
      this.teachDraft.y2 = pos.y
      this.redraw()
      return
    }

    if (this.isDragging && this.dragAnnotation) {
      const pos = this.canvasToImage(event)
      this.dragAnnotation.data_json.x = Math.round(pos.x)
      this.dragAnnotation.data_json.y = Math.round(pos.y)
      this.redraw()
      return
    }

    if (this.isPanning && this.lastMouse) {
      const dx = event.clientX - this.lastMouse.x
      const dy = event.clientY - this.lastMouse.y
      this.pan.x += dx
      this.pan.y += dy
      this.lastMouse = { x: event.clientX, y: event.clientY }
      this.redraw()
    }
  }

  canvasMouseUp(event) {
    if (this.teachDraft) {
      const x = Math.round(Math.min(this.teachDraft.x1, this.teachDraft.x2))
      const y = Math.round(Math.min(this.teachDraft.y1, this.teachDraft.y2))
      const w = Math.round(Math.abs(this.teachDraft.x2 - this.teachDraft.x1))
      const h = Math.round(Math.abs(this.teachDraft.y2 - this.teachDraft.y1))
      this.teachDraft = null
      if (w >= 15 && h >= 15) {
        this.teachCrops.push({ x, y, w, h })
        this.updateTeachPanel()
      }
      this.redraw()
      return
    }

    if (this.isDragging && this.dragAnnotation) {
      // Decide click-vs-drag from the cursor delta. A small delta means the
      // user wanted to review the marker, not move it.
      const dx = this.dragStart ? event.clientX - this.dragStart.clientX : 0
      const dy = this.dragStart ? event.clientY - this.dragStart.clientY : 0
      const clicked = Math.sqrt(dx * dx + dy * dy) < 5

      if (clicked) {
        this.openReviewPopover(this.dragAnnotation, event)
      } else {
        this.saveAnnotationPosition(this.dragAnnotation)
      }

      this.isDragging = false
      this.dragAnnotation = null
      this.dragStart = null
      this.canvas.style.cursor = this.currentTool === "pan" ? "grab" : "crosshair"
      return
    }

    if (this.isPanning) {
      this.isPanning = false
      this.canvas.style.cursor = this.currentTool === "pan" ? "grab" : "crosshair"
    }
  }

  findAnnotationAt(pos) {
    const hitRadius = 15 / this.zoom
    return this.annotations.find(a => {
      if (a.annotation_type !== "point_marker") return false
      const dx = a.data_json.x - pos.x
      const dy = a.data_json.y - pos.y
      return Math.sqrt(dx * dx + dy * dy) < hitRadius
    })
  }

  async saveAnnotationPosition(annotation) {
    const url = `${this.annotationsUrlValue}/${annotation.id}`
    await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
      },
      body: JSON.stringify({
        markup_annotation: {
          data_json: annotation.data_json
        }
      })
    })
  }

  handleWheel(event) {
    event.preventDefault()
    const rect = this.canvas.getBoundingClientRect()
    const mouseX = event.clientX - rect.left
    const mouseY = event.clientY - rect.top

    const oldZoom = this.zoom
    const factor = event.deltaY < 0 ? 1.1 : 0.9
    this.zoom = Math.max(0.1, Math.min(10, this.zoom * factor))

    // Zoom toward mouse position
    this.pan.x = mouseX - (mouseX - this.pan.x) * (this.zoom / oldZoom)
    this.pan.y = mouseY - (mouseY - this.pan.y) * (this.zoom / oldZoom)

    this.redraw()
  }

  canvasToImage(event) {
    const rect = this.canvas.getBoundingClientRect()
    const canvasX = event.clientX - rect.left
    const canvasY = event.clientY - rect.top
    return {
      x: (canvasX - this.pan.x) / this.zoom,
      y: (canvasY - this.pan.y) / this.zoom
    }
  }

  imageToCanvas(imgPos) {
    return {
      x: imgPos.x * this.zoom + this.pan.x,
      y: imgPos.y * this.zoom + this.pan.y
    }
  }

  // --- Annotations CRUD ---

  async loadAnnotations() {
    const resp = await fetch(this.annotationsUrlValue, {
      headers: { "Accept": "application/json" }
    })
    this.annotations = await resp.json()
    this.redraw()
    this.updateSidebar()
  }

  async createPointAnnotation(pos) {
    const label = this.pointTypeName(this.currentPointType)
    const body = {
      markup_annotation: {
        annotation_type: "point_marker",
        point_type: this.currentPointType,
        label: label,
        data_json: { x: Math.round(pos.x), y: Math.round(pos.y) }
      }
    }

    const resp = await fetch(this.annotationsUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
      },
      body: JSON.stringify(body)
    })

    if (resp.ok) {
      const annotation = await resp.json()
      this.annotations.push(annotation)
      this.redraw()
      this.updateSidebar()
    }
  }

  async finishRoute() {
    if (this.routePoints.length < 2) {
      this.routePoints = []
      this.redraw()
      return
    }

    const routeType = this.currentPointType || "general"
    const points = this.routePoints.map(p => ({ x: Math.round(p.x), y: Math.round(p.y) }))

    const body = {
      markup_annotation: {
        annotation_type: "cable_route",
        label: `${routeType} route`,
        point_type: routeType,
        data_json: { points: points }
      }
    }

    const resp = await fetch(this.annotationsUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
      },
      body: JSON.stringify(body)
    })

    if (resp.ok) {
      const annotation = await resp.json()
      this.annotations.push(annotation)
      this.routePoints = []
      this.redraw()
      this.updateSidebar()
    }
  }

  finishCurrentRoute() {
    this.finishRoute()
  }

  undoLastRoutePoint() {
    if (this.routePoints.length > 0) {
      this.routePoints.pop()
      this.redraw()
    }
  }

  // --- Line measurement tool ---

  async finishLine() {
    if (this.linePoints.length < 2) {
      this.linePoints = []
      this.redraw()
      return
    }

    const points = this.linePoints.map(p => ({ x: Math.round(p.x), y: Math.round(p.y) }))
    const lengthM = this.calcPolylineLength(points)
    const label = lengthM ? `${lengthM.toFixed(1)}m` : "Measurement"

    const body = {
      markup_annotation: {
        annotation_type: "line_measurement",
        label: label,
        data_json: { points: points }
      }
    }

    const resp = await fetch(this.annotationsUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
      },
      body: JSON.stringify(body)
    })

    if (resp.ok) {
      const annotation = await resp.json()
      this.annotations.push(annotation)
      this.linePoints = []
      this.redraw()
      this.updateSidebar()
    }
  }

  finishCurrentLine() {
    this.finishLine()
  }

  undoLastLinePoint() {
    if (this.linePoints.length > 0) {
      this.linePoints.pop()
      this.redraw()
    }
  }

  calcPolylineLength(points) {
    if (points.length < 2 || !this.pixelsPerMetreValue || this.pixelsPerMetreValue <= 0) return null
    let totalPx = 0
    for (let i = 1; i < points.length; i++) {
      const dx = points[i].x - points[i - 1].x
      const dy = points[i].y - points[i - 1].y
      totalPx += Math.sqrt(dx * dx + dy * dy)
    }
    return totalPx / this.pixelsPerMetreValue
  }

  // --- Area measurement tool ---

  async finishArea() {
    if (this.areaPoints.length < 3) {
      this.areaPoints = []
      this.redraw()
      return
    }

    const points = this.areaPoints.map(p => ({ x: Math.round(p.x), y: Math.round(p.y) }))
    const areaM2 = this.calcPolygonArea(points)
    const label = areaM2 ? `${areaM2.toFixed(1)} m²` : "Area"

    const body = {
      markup_annotation: {
        annotation_type: "area_measurement",
        label: label,
        data_json: { points: points }
      }
    }

    const resp = await fetch(this.annotationsUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
      },
      body: JSON.stringify(body)
    })

    if (resp.ok) {
      const annotation = await resp.json()
      this.annotations.push(annotation)
      this.areaPoints = []
      this.redraw()
      this.updateSidebar()
    }
  }

  finishCurrentArea() {
    this.finishArea()
  }

  undoLastAreaPoint() {
    if (this.areaPoints.length > 0) {
      this.areaPoints.pop()
      this.redraw()
    }
  }

  calcPolygonArea(points) {
    if (points.length < 3 || !this.pixelsPerMetreValue || this.pixelsPerMetreValue <= 0) return null
    const n = points.length
    let sum = 0
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n
      sum += points[i].x * points[j].y
      sum -= points[j].x * points[i].y
    }
    const areaPx = Math.abs(sum) / 2
    return areaPx / (this.pixelsPerMetreValue * this.pixelsPerMetreValue)
  }

  async deleteAnnotation(event) {
    const id = event.currentTarget.dataset.annotationId
    const url = `${this.annotationsUrlValue}/${id}`

    const resp = await fetch(url, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
      }
    })

    if (resp.ok) {
      this.annotations = this.annotations.filter(a => a.id !== parseInt(id))
      this.redraw()
      this.updateSidebar()
    }
  }

  // --- Scale calibration ---

  async promptScaleDistance() {
    const p1 = this.scalePoints[0]
    const p2 = this.scalePoints[1]
    const pixelDist = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2)

    const metres = prompt("Enter the real-world distance of this line in metres:")
    if (!metres || parseFloat(metres) <= 0) {
      this.scalePoints = []
      this.redraw()
      return
    }

    const resp = await fetch(this.scaleUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
      },
      body: JSON.stringify({ pixels_distance: pixelDist, known_metres: parseFloat(metres) })
    })

    if (resp.ok) {
      const data = await resp.json()
      this.pixelsPerMetreValue = data.pixels_per_metre

      // Save as annotation for visual reference
      await fetch(this.annotationsUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        },
        body: JSON.stringify({
          markup_annotation: {
            annotation_type: "scale_reference",
            label: `${metres}m reference`,
            data_json: {
              points: [
                { x: Math.round(p1.x), y: Math.round(p1.y) },
                { x: Math.round(p2.x), y: Math.round(p2.y) }
              ],
              metres: parseFloat(metres)
            }
          }
        })
      })

      await this.loadAnnotations()
    }

    this.scalePoints = []
    this.currentTool = null
    this.redraw()
    this.updateSidebar()
  }

  // --- Drawing ---

  redraw() {
    const ctx = this.ctx
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)

    // Draw image
    ctx.save()
    ctx.translate(this.pan.x, this.pan.y)
    ctx.scale(this.zoom, this.zoom)
    ctx.drawImage(this.img, 0, 0)

    // Draw saved annotations
    this.annotations.forEach(a => {
      if (a.annotation_type === "point_marker") {
        this.drawPointMarker(ctx, a)
      } else if (a.annotation_type === "cable_route") {
        this.drawRoute(ctx, a.data_json.points, this.routeColor(a.point_type), false)
      } else if (a.annotation_type === "line_measurement") {
        this.drawMeasurementLine(ctx, a.data_json.points, a.label, false)
      } else if (a.annotation_type === "area_measurement") {
        this.drawAreaPolygon(ctx, a.data_json.points, a.label, false)
      } else if (a.annotation_type === "scale_reference") {
        this.drawScaleLine(ctx, a.data_json.points, a.label)
      }
    })

    // Draw in-progress route
    if (this.routePoints.length > 0) {
      this.drawRoute(ctx, this.routePoints, "#EF4444", true)
    }

    // Draw in-progress line measurement
    if (this.linePoints.length > 0) {
      const liveLength = this.calcPolylineLength(this.linePoints)
      const liveLabel = liveLength ? `${liveLength.toFixed(1)}m` : "measuring..."
      this.drawMeasurementLine(ctx, this.linePoints, liveLabel, true)
    }

    // Draw in-progress area
    if (this.areaPoints.length > 0) {
      const liveArea = this.calcPolygonArea(this.areaPoints)
      const liveLabel = liveArea ? `${liveArea.toFixed(1)} m²` : "drawing..."
      this.drawAreaPolygon(ctx, this.areaPoints, liveLabel, true)
    }

    // Draw in-progress scale
    if (this.scalePoints.length > 0) {
      this.drawScaleLine(ctx, this.scalePoints, "measuring...")
    }

    // Draw teach marquees (committed + in-progress)
    this.teachCrops.forEach((c, i) => this.drawTeachRect(ctx, c, i + 1, false))
    if (this.teachDraft) {
      const d = this.teachDraft
      const rect = {
        x: Math.min(d.x1, d.x2),
        y: Math.min(d.y1, d.y2),
        w: Math.abs(d.x2 - d.x1),
        h: Math.abs(d.y2 - d.y1)
      }
      this.drawTeachRect(ctx, rect, this.teachCrops.length + 1, true)
    }

    ctx.restore()
  }

  // --- Teach panel integration ---

  // Keeps the teach panel in sync with controller state: active point type,
  // example count, and the hidden form fields submitted to teach_symbol.
  updateTeachPanel() {
    const panel = document.getElementById("teach-panel")
    if (!panel) return

    const countEl = panel.querySelector("[data-teach-count]")
    if (countEl) countEl.textContent = this.teachCrops.length

    const bboxField = panel.querySelector("[data-teach-bboxes]")
    if (bboxField) bboxField.value = JSON.stringify(this.teachCrops)

    // The point type is now scoped via the row the user clicked — push it
    // into the hidden form field and render the name inline.
    const pointTypeField = panel.querySelector("[data-teach-point-type]")
    if (pointTypeField) pointTypeField.value = this.currentPointType || ""

    const nameEl = panel.querySelector("[data-teach-point-type-name]")
    if (nameEl) nameEl.textContent = this.pointTypeName(this.currentPointType) || "—"

    const submitBtn = panel.querySelector("[data-teach-submit]")
    if (submitBtn) submitBtn.disabled = this.teachCrops.length === 0 || !this.currentPointType

    const active = this.currentTool === "teach" && !!this.currentPointType
    panel.classList.toggle("hidden", !active)

    // Existing-examples strip: show what the account already has for this
    // point type so the user doesn't re-teach redundant crops. Driven by
    // glyphsByType[currentPointType].examples populated on loadGlyphs.
    this.hydrateTeachExistingExamples()

    // Ring the row whose type is currently being taught so it's obvious
    // which one is scoped — survives across tool switches until the user
    // explicitly picks another row.
    this.element.querySelectorAll("[data-point-row]").forEach(row => {
      const isActive = active && row.dataset.pointRow === this.currentPointType
      row.classList.toggle("ring-2", isActive)
      row.classList.toggle("ring-mint", isActive)
    })
  }

  // Photoshop-style visibility toggle per point type. Kept client-side only:
  // the state matters while the user is scrubbing through layers, not across
  // refreshes, and the DB already owns the "is this annotation present" fact.
  toggleVisibility(event) {
    event.preventDefault()
    const button = event.currentTarget
    const pointType = button.dataset.pointType
    if (!pointType) return

    const wasHidden = this.hiddenPointTypes.has(pointType)
    if (wasHidden) {
      this.hiddenPointTypes.delete(pointType)
    } else {
      this.hiddenPointTypes.add(pointType)
    }
    const nowHidden = !wasHidden

    // Swap the eye / eye-off icons inside this button.
    const eyeOn = button.querySelector("[data-visibility-on]")
    const eyeOff = button.querySelector("[data-visibility-off]")
    if (eyeOn) eyeOn.classList.toggle("hidden", nowHidden)
    if (eyeOff) eyeOff.classList.toggle("hidden", !nowHidden)

    // Dim the row a touch so the visibility state is scannable at a glance.
    const row = button.closest("[data-point-row]")
    if (row) row.classList.toggle("opacity-40", nowHidden)

    this.redraw()
  }

  // Marker review popover — appears at the click location when the user
  // taps an existing marker without dragging it. Lets them accept (confirm
  // the placement) or reject (delete). Accept/reject also feed accepted_count
  // / rejected_count on the originating SymbolTeaching so the trust score
  // improves with use.
  openReviewPopover(annotation, event) {
    const popover = document.getElementById("marker-review-popover")
    if (!popover) return

    this.reviewAnnotation = annotation

    // Position near the cursor, constrained to the canvas bounds.
    const rect = this.canvas.getBoundingClientRect()
    const parentRect = popover.parentElement.getBoundingClientRect()
    const left = Math.min(Math.max(event.clientX - parentRect.left + 10, 10),
                           parentRect.width - 220)
    const top = Math.min(Math.max(event.clientY - parentRect.top + 10, 10),
                          parentRect.height - 160)
    popover.style.left = `${left}px`
    popover.style.top = `${top}px`

    // Populate contents.
    const nameEl = popover.querySelector("[data-mr-name]")
    const sourceEl = popover.querySelector("[data-mr-source]")
    const thumbEl = popover.querySelector("[data-mr-thumb]")
    const acceptBtn = popover.querySelector("[data-mr-accept]")

    if (nameEl) nameEl.textContent = this.pointTypeName(annotation.point_type) || annotation.point_type
    if (sourceEl) sourceEl.textContent = this.describeAnnotationSource(annotation)

    const g = this.glyphsByType[annotation.point_type]
    if (thumbEl) {
      if (g && g.thumbnail_url) {
        thumbEl.src = g.thumbnail_url
        thumbEl.classList.remove("hidden")
      } else {
        thumbEl.classList.add("hidden")
      }
    }

    // Accept is a no-op for already-confirmed manual markers — hide the button.
    if (acceptBtn) acceptBtn.classList.toggle("hidden", !annotation.generated)

    popover.classList.remove("hidden")
  }

  closeReviewPopover() {
    const popover = document.getElementById("marker-review-popover")
    if (popover) popover.classList.add("hidden")
    this.reviewAnnotation = null
  }

  describeAnnotationSource(annotation) {
    const d = annotation.data_json || {}
    if (!annotation.generated) return "Manually placed"
    if (d.source === "teach_by_example") return "Taught by you"
    if (d.template_matched) return "AI + template match"
    return "AI detected"
  }

  async acceptReviewedMarker(event) {
    if (event) event.preventDefault()
    const a = this.reviewAnnotation
    if (!a) return

    const url = `${window.location.pathname}/accept_detection`
    const resp = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
      },
      body: JSON.stringify({ annotation_id: a.id })
    })
    if (resp.ok) {
      a.generated = false
      this.redraw()
    }
    this.closeReviewPopover()
  }

  async rejectReviewedMarker(event) {
    if (event) event.preventDefault()
    const a = this.reviewAnnotation
    if (!a) return

    const url = `${window.location.pathname}/reject_detection`
    const resp = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
      },
      body: JSON.stringify({ annotation_id: a.id })
    })
    if (resp.ok) {
      this.annotations = this.annotations.filter(x => x.id !== a.id)
      this.redraw()
    }
    this.closeReviewPopover()
  }

  showAllPointTypes(event) {
    if (event) event.preventDefault()
    this.hiddenPointTypes.clear()
    this.syncVisibilityUi()
    this.redraw()
  }

  hideAllPointTypes(event) {
    if (event) event.preventDefault()
    this.element.querySelectorAll("[data-point-row]").forEach(row => {
      this.hiddenPointTypes.add(row.dataset.pointRow)
    })
    this.syncVisibilityUi()
    this.redraw()
  }

  // Reconcile each row's eye icon + dimming with the current hiddenPointTypes
  // set. Called after bulk show/hide; the per-row toggle action handles its
  // own UI directly.
  syncVisibilityUi() {
    this.element.querySelectorAll("[data-point-row]").forEach(row => {
      const pt = row.dataset.pointRow
      const isHidden = this.hiddenPointTypes.has(pt)
      row.classList.toggle("opacity-40", isHidden)
      const button = row.querySelector("[data-action*='toggleVisibility']")
      if (button) {
        const eyeOn = button.querySelector("[data-visibility-on]")
        const eyeOff = button.querySelector("[data-visibility-off]")
        if (eyeOn) eyeOn.classList.toggle("hidden", isHidden)
        if (eyeOff) eyeOff.classList.toggle("hidden", !isHidden)
      }
    })
  }

  clearTeach(event) {
    if (event) event.preventDefault()
    this.teachCrops = []
    this.teachDraft = null
    this.updateTeachPanel()
    this.redraw()
  }

  undoTeachCrop(event) {
    if (event) event.preventDefault()
    this.teachCrops.pop()
    this.updateTeachPanel()
    this.redraw()
  }

  drawTeachRect(ctx, rect, label, inProgress) {
    ctx.save()
    ctx.strokeStyle = inProgress ? "#FF7A1A" : "#16D2A6"
    ctx.lineWidth = 2 / this.zoom
    if (inProgress) ctx.setLineDash([ 6 / this.zoom, 4 / this.zoom ])
    ctx.strokeRect(rect.x, rect.y, rect.w, rect.h)
    ctx.setLineDash([])

    // Label in top-left
    const fontSize = 14 / this.zoom
    ctx.font = `bold ${fontSize}px sans-serif`
    ctx.fillStyle = inProgress ? "#FF7A1A" : "#16D2A6"
    ctx.fillText(`#${label}`, rect.x + 4 / this.zoom, rect.y + fontSize)
    ctx.restore()
  }

  drawPointMarker(ctx, annotation) {
    if (this.hiddenPointTypes.has(annotation.point_type)) return

    const x = annotation.data_json.x
    const y = annotation.data_json.y
    const color = this.pointColor(annotation.point_type)
    const isAI = annotation.generated
    const r = (isAI ? 10 : 8) / this.zoom

    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)

    if (isAI) {
      const isTemplateMatched = annotation.data_json.template_matched

      if (isTemplateMatched) {
        // Template-matched: solid fill but with distinct border to show it's AI
        ctx.fillStyle = color
        ctx.globalAlpha = 0.6
        ctx.fill()
        ctx.globalAlpha = 1
        ctx.strokeStyle = "#FFFFFF"
        ctx.lineWidth = 2.5 / this.zoom
        ctx.stroke()
        ctx.strokeStyle = color
        ctx.lineWidth = 1 / this.zoom
        ctx.stroke()
      } else {
        // AI-only position: dashed outline, semi-transparent
        ctx.fillStyle = color
        ctx.globalAlpha = 0.3
        ctx.fill()
        ctx.globalAlpha = 0.9
        ctx.strokeStyle = color
        ctx.lineWidth = 2 / this.zoom
        ctx.setLineDash([4 / this.zoom, 3 / this.zoom])
        ctx.stroke()
        ctx.setLineDash([])
        ctx.globalAlpha = 1
      }

      // Badge
      const badge = isTemplateMatched ? "CV" : "AI"
      ctx.font = `bold ${Math.max(8, 9 / this.zoom)}px sans-serif`
      ctx.fillStyle = color
      ctx.fillText(badge, x + r + 2, y - 2)
      ctx.font = `${Math.max(9, 11 / this.zoom)}px sans-serif`
      ctx.fillStyle = "#6B7280"
      ctx.fillText(this.shortLabel(annotation.point_type), x + r + 2, y + 8)
    } else {
      ctx.fillStyle = color
      ctx.globalAlpha = 0.8
      ctx.fill()
      ctx.globalAlpha = 1
      ctx.strokeStyle = "#FFFFFF"
      ctx.lineWidth = 2 / this.zoom
      ctx.stroke()

      // Label
      ctx.font = `${Math.max(10, 12 / this.zoom)}px sans-serif`
      ctx.fillStyle = "#1F2937"
      ctx.fillText(this.shortLabel(annotation.point_type), x + r + 2, y + 4)
    }
  }

  drawRoute(ctx, points, color, inProgress) {
    if (points.length < 1) return

    ctx.beginPath()
    ctx.moveTo(points[0].x, points[0].y)
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y)
    }
    ctx.strokeStyle = color
    ctx.lineWidth = (inProgress ? 3 : 2.5) / this.zoom
    if (inProgress) ctx.setLineDash([8 / this.zoom, 4 / this.zoom])
    ctx.stroke()
    ctx.setLineDash([])

    // Draw dots at each point
    points.forEach(p => {
      ctx.beginPath()
      ctx.arc(p.x, p.y, 4 / this.zoom, 0, Math.PI * 2)
      ctx.fillStyle = color
      ctx.fill()
    })
  }

  drawMeasurementLine(ctx, points, label, inProgress) {
    if (points.length < 1) return

    // Draw the polyline
    ctx.beginPath()
    ctx.moveTo(points[0].x, points[0].y)
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y)
    }
    ctx.strokeStyle = "#10B981"
    ctx.lineWidth = (inProgress ? 3 : 2.5) / this.zoom
    if (inProgress) ctx.setLineDash([8 / this.zoom, 4 / this.zoom])
    ctx.stroke()
    ctx.setLineDash([])

    // Draw dots at each point
    points.forEach(p => {
      ctx.beginPath()
      ctx.arc(p.x, p.y, 4 / this.zoom, 0, Math.PI * 2)
      ctx.fillStyle = "#10B981"
      ctx.fill()
    })

    // Draw length label at midpoint of the line
    if (label && points.length >= 2) {
      const mid = Math.floor(points.length / 2)
      const mx = (points[mid - 1].x + points[mid].x) / 2
      const my = (points[mid - 1].y + points[mid].y) / 2

      const fontSize = Math.max(11, 13 / this.zoom)
      ctx.font = `bold ${fontSize}px sans-serif`

      // Background pill
      const textWidth = ctx.measureText(label).width
      const pad = 4 / this.zoom
      ctx.fillStyle = "rgba(255,255,255,0.85)"
      ctx.beginPath()
      ctx.roundRect(mx - textWidth / 2 - pad, my - fontSize / 2 - pad, textWidth + pad * 2, fontSize + pad * 2, 3 / this.zoom)
      ctx.fill()

      // Text
      ctx.fillStyle = "#047857"
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.fillText(label, mx, my)
      ctx.textAlign = "start"
      ctx.textBaseline = "alphabetic"
    }
  }

  drawAreaPolygon(ctx, points, label, inProgress) {
    if (points.length < 2) return

    // Fill
    ctx.beginPath()
    ctx.moveTo(points[0].x, points[0].y)
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y)
    }
    ctx.closePath()
    ctx.fillStyle = inProgress ? "rgba(59, 130, 246, 0.08)" : "rgba(59, 130, 246, 0.12)"
    ctx.fill()

    // Stroke
    ctx.strokeStyle = "#3B82F6"
    ctx.lineWidth = (inProgress ? 2.5 : 2) / this.zoom
    if (inProgress) ctx.setLineDash([8 / this.zoom, 4 / this.zoom])
    ctx.stroke()
    ctx.setLineDash([])

    // Dots
    points.forEach(p => {
      ctx.beginPath()
      ctx.arc(p.x, p.y, 4 / this.zoom, 0, Math.PI * 2)
      ctx.fillStyle = "#3B82F6"
      ctx.fill()
    })

    // Label at centroid
    if (label && points.length >= 3) {
      const cx = points.reduce((s, p) => s + p.x, 0) / points.length
      const cy = points.reduce((s, p) => s + p.y, 0) / points.length

      const fontSize = Math.max(11, 13 / this.zoom)
      ctx.font = `bold ${fontSize}px sans-serif`

      const textWidth = ctx.measureText(label).width
      const pad = 4 / this.zoom
      ctx.fillStyle = "rgba(255,255,255,0.85)"
      ctx.beginPath()
      ctx.roundRect(cx - textWidth / 2 - pad, cy - fontSize / 2 - pad, textWidth + pad * 2, fontSize + pad * 2, 3 / this.zoom)
      ctx.fill()

      ctx.fillStyle = "#1D4ED8"
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.fillText(label, cx, cy)
      ctx.textAlign = "start"
      ctx.textBaseline = "alphabetic"
    }
  }

  drawScaleLine(ctx, points, label) {
    if (points.length < 1) return
    ctx.beginPath()
    ctx.moveTo(points[0].x, points[0].y)
    if (points.length > 1) ctx.lineTo(points[1].x, points[1].y)
    ctx.strokeStyle = "#F59E0B"
    ctx.lineWidth = 2 / this.zoom
    ctx.setLineDash([6 / this.zoom, 3 / this.zoom])
    ctx.stroke()
    ctx.setLineDash([])

    if (label && points.length === 2) {
      const mx = (points[0].x + points[1].x) / 2
      const my = (points[0].y + points[1].y) / 2
      ctx.font = `bold ${Math.max(11, 13 / this.zoom)}px sans-serif`
      ctx.fillStyle = "#F59E0B"
      ctx.fillText(label, mx + 5, my - 5)
    }
  }

  // --- Colors & labels ---

  _pointTypeConfig(slug) {
    return this.pointTypesValue.find(pt => pt.slug === slug)
  }

  pointColor(pointType) {
    return this._pointTypeConfig(pointType)?.colour || "#6B7280"
  }

  routeColor(routeType) {
    return this.pointColor(routeType) || "#8B5CF6"
  }

  shortLabel(pointType) {
    return this._pointTypeConfig(pointType)?.shortLabel || "?"
  }

  pointTypeName(pointType) {
    return this._pointTypeConfig(pointType)?.name || pointType.replace(/_/g, " ")
  }

  // --- Sidebar ---

  updateSidebar() {
    // Point counts
    const counts = {}
    this.annotations.filter(a => a.annotation_type === "point_marker").forEach(a => {
      counts[a.point_type] = (counts[a.point_type] || 0) + 1
    })

    let countsHtml = ""
    if (Object.keys(counts).length === 0) {
      countsHtml = '<p class="text-sm text-gray-500">No points marked yet.</p>'
    } else {
      countsHtml = '<ul class="space-y-1">'
      for (const [type, count] of Object.entries(counts).sort()) {
        const color = this.pointColor(type)
        countsHtml += `<li class="flex items-center justify-between text-sm">
          <span class="flex items-center gap-2">
            <span class="inline-block w-3 h-3 rounded-full" style="background:${color}"></span>
            ${this.pointTypeName(type)}
          </span>
          <span class="font-medium">${count}</span>
        </li>`
      }
      countsHtml += "</ul>"
    }
    if (this.hasPointCountsTarget) this.pointCountsTarget.innerHTML = countsHtml

    // Routes
    const routes = this.annotations.filter(a => a.annotation_type === "cable_route")
    let routeHtml = ""
    if (routes.length === 0) {
      routeHtml = '<p class="text-sm text-gray-500">No routes drawn yet.</p>'
    } else {
      routeHtml = '<ul class="space-y-1">'
      routes.forEach(r => {
        const len = r.route_length_metres != null && r.route_length_metres > 0 ? `${r.route_length_metres}m` : "no length"
        const cableType = r.cable_type ? `<br><span class="text-xs text-gray-400">${r.cable_type}</span>` : ""
        routeHtml += `<li class="text-sm border-b border-gray-50 pb-1">
          <div class="flex items-center justify-between">
            <span>${r.label}</span>
            <span class="flex items-center gap-2">
              <span class="font-medium">${len}</span>
              <button data-action="sheet-markup#deleteAnnotation" data-annotation-id="${r.id}" class="text-red-500 hover:text-red-700 text-xs">✕</button>
            </span>
          </div>
          ${cableType}
        </li>`
      })
      routeHtml += "</ul>"
    }
    if (this.hasRouteListTarget) this.routeListTarget.innerHTML = routeHtml

    // Measurements
    const measurements = this.annotations.filter(a => a.annotation_type === "line_measurement")
    let measureHtml = ""
    if (measurements.length === 0) {
      measureHtml = '<p class="text-sm text-gray-500">No measurements yet.</p>'
    } else {
      let totalM = 0
      measureHtml = '<ul class="space-y-1">'
      measurements.forEach(m => {
        const len = m.route_length_metres != null && m.route_length_metres > 0 ? `${m.route_length_metres}m` : "—"
        if (m.route_length_metres > 0) totalM += m.route_length_metres
        measureHtml += `<li class="text-sm border-b border-gray-50 pb-1">
          <div class="flex items-center justify-between">
            <span class="flex items-center gap-2">
              <span class="inline-block w-3 h-3 rounded-full" style="background:#10B981"></span>
              ${m.label || 'Line'}
            </span>
            <span class="flex items-center gap-2">
              <span class="font-medium">${len}</span>
              <button data-action="sheet-markup#deleteAnnotation" data-annotation-id="${m.id}" class="text-red-500 hover:text-red-700 text-xs">✕</button>
            </span>
          </div>
        </li>`
      })
      measureHtml += "</ul>"
      if (totalM > 0) {
        measureHtml += `<p class="mt-2 text-sm font-semibold text-gray-900">Total: ${totalM.toFixed(1)}m</p>`
      }
    }
    if (this.hasMeasurementListTarget) this.measurementListTarget.innerHTML = measureHtml

    // Areas
    const areas = this.annotations.filter(a => a.annotation_type === "area_measurement")
    let areaHtml = ""
    if (areas.length === 0) {
      areaHtml = '<p class="text-xs text-slate-400">No areas yet.</p>'
    } else {
      let totalM2 = 0
      areaHtml = '<div class="space-y-2">'
      areas.forEach(a => {
        const area = a.area_sq_metres != null && a.area_sq_metres > 0 ? `${a.area_sq_metres} m²` : "—"
        if (a.area_sq_metres > 0) totalM2 += a.area_sq_metres
        areaHtml += `<div class="rounded-2xl border border-line p-3">
          <div class="flex items-center justify-between">
            <div>
              <div class="font-bold text-ink text-sm">${a.label || 'Area'}</div>
              <div class="text-xs text-slate-500">Area measurement</div>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-lg font-black text-ink">${area}</span>
              <button data-action="sheet-markup#deleteAnnotation" data-annotation-id="${a.id}" class="text-red-500 hover:text-red-700 text-xs">✕</button>
            </div>
          </div>
        </div>`
      })
      areaHtml += "</div>"
      if (totalM2 > 0) {
        areaHtml += `<p class="mt-2 text-sm font-black text-ink">Total: ${totalM2.toFixed(1)} m²</p>`
      }
    }
    if (this.hasAreaListTarget) this.areaListTarget.innerHTML = areaHtml

    // Scale pill — keep markup consistent with the server-rendered version in
    // the toolbar (see drawing_sheets/show.html.erb) so calibrating in-browser
    // doesn't reset the styling.
    if (this.hasScaleInfoTarget) {
      if (this.pixelsPerMetreValue > 0) {
        this.scaleInfoTarget.innerHTML =
          `<span class="h-1.5 w-1.5 rounded-full bg-mint"></span>` +
          `<span class="font-semibold text-ink">Scale:</span>` +
          `<span class="text-slate-600">${this.pixelsPerMetreValue.toFixed(1)} px/m</span>`
      } else {
        this.scaleInfoTarget.innerHTML =
          `<span class="h-1.5 w-1.5 rounded-full bg-slate-300"></span>` +
          `<span class="font-semibold text-slate-500">Scale not set</span>`
      }
    }
  }

  // --- Keyboard shortcuts ---

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.currentTool = null
      this.routePoints = []
      this.linePoints = []
      this.areaPoints = []
      this.scalePoints = []
      this.redraw()
    } else if (event.key === "Enter" && this.currentTool === "cable_route") {
      this.finishRoute()
    } else if (event.key === "Enter" && this.currentTool === "line") {
      this.finishLine()
    } else if (event.key === "Enter" && this.currentTool === "area") {
      this.finishArea()
    } else if (event.key === "z" && (event.ctrlKey || event.metaKey) && this.currentTool === "cable_route") {
      this.undoLastRoutePoint()
    } else if (event.key === "z" && (event.ctrlKey || event.metaKey) && this.currentTool === "line") {
      this.undoLastLinePoint()
    } else if (event.key === "z" && (event.ctrlKey || event.metaKey) && this.currentTool === "area") {
      this.undoLastAreaPoint()
    }
  }

  resetView() {
    this.fitImageToCanvas()
  }
}
